import { useEffect, useState } from 'react';

import { Button, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import AsyncBulkUpload from './AsyncBulkUpload';
import { EO_JOB_STATUS, EO_JOB_STATUS_MAPPING, JOB_TYPES, POLLING_FREQ, UI_JOB_STATUS } from './BulkUploaderUtils';
import BULK_UPLOADER_TITLE from './constants';
import EODispatchInfoMessage from './EODispatchInfoMessage';
import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError, validator } from '../../clientUtils';
import { FILE_TYPE_ATTRIBUTE } from '../../constants';

const JOB_NAME = {
  validate: 'POSI_EO_BULK_DISPATCH_VALIDATE',
  submit: 'POSI_EO_BULK_DISPATCH_SUBMIT'
};

const ATTACHMENT_TYPE = {
  validation: 'EO_BULK_DISPATCH_VALIDATION',
  submit: 'EO_BULK_DISPATCH_SUBMIT'
};

const onError = (error) => message.error(getError(error));

/**
 * Get request body for validate/submit API calls
 */
const getBody = (jobType, fileLink) => {
  if (jobType === JOB_TYPES.validation) {
    return {
      job_name: JOB_NAME.validate,
      files: [
        {
          attachment_type: ATTACHMENT_TYPE.validation,
          file_url: fileLink
        }
      ]
    };
  }
  return {
    job_name: JOB_NAME.submit,
    files: [
      {
        attachment_type: ATTACHMENT_TYPE.submit,
        file_url: fileLink
      }
    ]
  };
};

/**
 * Determine UI job status based on API response
 */
const getEOJobType = (status, jobName, validationSummary) => {
  const loadingStatuses = [EO_JOB_STATUS.requestReceived, EO_JOB_STATUS.inProgress];

  if (jobName.includes('VALIDATE')) {
    if (status === EO_JOB_STATUS.failed) return UI_JOB_STATUS.errorOnExecution;
    if (loadingStatuses.includes(status)) return UI_JOB_STATUS.validating;

    // Check if validation has failures
    if (validationSummary?.failure > 0) return UI_JOB_STATUS.validationFailed;

    return UI_JOB_STATUS.validated;
  }

  if (jobName.includes('SUBMIT')) {
    if (status === EO_JOB_STATUS.failed) return UI_JOB_STATUS.submitFailed;
    if (loadingStatuses.includes(status)) return UI_JOB_STATUS.submitting;
    return UI_JOB_STATUS.submitted;
  }

  return null;
};

const EODispatch = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [sampleDownloadLink, setSampleDownloadLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [buttonLoading, setButtonLoading] = useState(false);

  // Fetch sample file link on mount
  useEffect(() => {
    setFiles([]);
    setJobStatus(UI_JOB_STATUS.uploading);

    CallApi(
      apiConfig.eoBulkDispatch.sample,
      null,
      null,
      setLoading,
      (res) => setSampleDownloadLink(res),
      () => message.error('Unable to download sample file!')
    );
  }, []);

  // Poll job status if id is present
  useEffect(() => {
    let pollingId;

    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.details, path: apiConfig.job.details.path(id) },
          null,
          null,
          null,
          (res) => {
            const { job_name, job_status, response } = res;

            // Stop polling if job is complete
            if (EO_JOB_STATUS_MAPPING[job_status] === 'end' && pollingId) {
              clearInterval(pollingId);
            }

            // Handle successful submission - redirect to international sales orders
            if (job_name.includes('SUBMIT') && job_status === EO_JOB_STATUS.successful) {
              message.success('Dispatch completed successfully!');
              setTimeout(() => navigate('/sales/international-sales-order'), 2000);
              return;
            }

            // Extract file info from response
            const file = response?.files?.find(
              (f) => f.attachment_type === ATTACHMENT_TYPE.validation || f.attachment_type === ATTACHMENT_TYPE.submit
            );

            if (file) {
              setFiles([
                {
                  ...file,
                  summary: file?.validation_summary,
                  link: file?.file_url,
                  fileLink: file?.file_url,
                  message: file?.failure_message
                }
              ]);
            }

            setJobStatus(getEOJobType(job_status, job_name, file?.validation_summary));
          },
          onError
        );
      };

      getJobStatus();
      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }

    return () => clearInterval(pollingId);
  }, [navigate, id]);

  /**
   * Handle file upload - uploads to S3 via pre-signed URL
   */
  const handleFileUpload = async (file) => {
    try {
      // Get pre-signed URL
      const preSignedUrlResponse = await new Promise((resolve, reject) => {
        CallApi(apiConfig.eoBulkDispatch.getPresignedURL, { job_name: JOB_NAME.validate }, null, null, resolve, reject);
      });

      // Upload file to S3
      await fetch(preSignedUrlResponse, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'text/csv'
        }
      });

      // Extract S3 URL (without query params)
      const s3Url = preSignedUrlResponse.split('?')[0];

      setFiles([{ ...file, fileLink: s3Url }]);
      setJobStatus(UI_JOB_STATUS.uploaded);
    } catch (error) {
      onError(error);
    }
  };

  const uploadProps = {
    title: 'EO Dispatch File',
    description: 'Please upload the dispatch file (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files,
    validator,
    onUpload: handleFileUpload,
    onRemove: async () => {
      setFiles([]);
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: [UI_JOB_STATUS.submitted, UI_JOB_STATUS.submitting, UI_JOB_STATUS.validating].includes(jobStatus)
  };

  const fileUploaderProps = {
    message: '',
    items: [uploadProps]
  };

  /**
   * Handle Run Validation / Submit button click
   */
  const handleClick = () => {
    // If file is uploaded, run validation
    if (jobStatus === UI_JOB_STATUS.uploaded) {
      CallApi(
        apiConfig.eoBulkDispatch.validate,
        null,
        getBody(JOB_TYPES.validation, files?.[0]?.fileLink),
        setButtonLoading,
        (res) => {
          navigate(`/bulkuploader/eo-dispatch/${res.id}`);
        },
        onError
      );
      return;
    }

    // If validated, submit for dispatch
    if (jobStatus === UI_JOB_STATUS.validated) {
      CallApi(
        apiConfig.eoBulkDispatch.submit,
        null,
        getBody(JOB_TYPES.submit, files?.[0]?.fileLink),
        setButtonLoading,
        (res) => {
          message.success('Dispatch job submitted');
          navigate(`/bulkuploader/eo-dispatch/${res.id}`);
        },
        onError
      );
    }
  };

  /**
   * Determine if button should be disabled
   */
  const isButtonDisabled = () => {
    if (jobStatus === UI_JOB_STATUS.validated) {
      // Disable if there are validation failures
      return files?.[0]?.summary?.failure > 0;
    }
    if (jobStatus === UI_JOB_STATUS.uploaded) return false;
    return true;
  };

  /**
   * Get button text based on current status
   */
  const getButtonText = () => {
    const validationStates = [
      UI_JOB_STATUS.uploading,
      UI_JOB_STATUS.uploaded,
      UI_JOB_STATUS.validating,
      UI_JOB_STATUS.validationFailed
    ];
    if (validationStates.includes(jobStatus)) return 'Run Validation';
    return 'Submit';
  };

  return (
    <AsyncBulkUpload
      button={
        <Button disabled={isButtonDisabled()} loading={buttonLoading} onClick={handleClick} type="primary">
          {getButtonText()}
        </Button>
      }
      group={['bulkUploaders', 'international_so']}
      info={<EODispatchInfoMessage failureMessage={files?.[0]?.message} status={jobStatus} />}
      loading={loading}
      menuKey="eo-dispatch-bulk"
      title={BULK_UPLOADER_TITLE['eo-dispatch']}
      uploadersProp={[fileUploaderProps]}
    />
  );
};

export default EODispatch;
